import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchBaseballFixtureContext,
  fetchDailyFixtures,
} from "../api";

const C = {
  page: "#07110d",
  panel: "linear-gradient(180deg, rgba(12,24,19,0.96), rgba(7,15,12,0.98))",
  card: "rgba(255,255,255,0.03)",
  border: "rgba(148,163,184,0.18)",
  text: "#e7f5ec",
  sub: "#95ab9d",
  green: "#31d48b",
  greenDeep: "#16b96b",
  amber: "#fbbf24",
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const WATCHLIST_KEY = "baseball_watchlist_v1";

const PANEL = {
  border: `1px solid ${C.border}`,
  borderRadius: 18,
  background: C.panel,
  boxShadow: "0 12px 26px rgba(0,0,0,0.22)",
};

const BUTTON = {
  height: 36,
  borderRadius: 10,
  border: `1px solid ${C.border}`,
  background: "rgba(255,255,255,0.06)",
  color: C.text,
  padding: "0 12px",
  fontWeight: 700,
  cursor: "pointer",
};

const PRIMARY = {
  ...BUTTON,
  border: "1px solid rgba(49,212,139,0.7)",
  background: "linear-gradient(180deg, #31d48b, #16b96b)",
  color: "#062114",
};

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const toArray = (value) => (Array.isArray(value) ? value : []);

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Europe/London",
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const fmtStatus = (fixture = {}, context = null) => {
  const detailed = firstDefined(context?.status?.detailed, context?.status?.abstract);
  if (detailed) return detailed;
  if (fixture.status === "LIVE") return `Live${fixture.elapsed ? ` • ${fixture.elapsed}m` : ""}`;
  if (fixture.status === "FT") return "Final";
  if (fixture.status === "NS") return "Scheduled";
  return firstDefined(fixture.status, "Scheduled");
};

const getProbablePitcher = (context, side) =>
  firstDefined(
    context?.probable_pitchers?.[side],
    context?.teams?.[side]?.probable_pitcher,
    context?.teams?.[side]?.probablePitcher,
    null
  );

const inferPropChipLabels = (rows) => {
  const text = rows.map((row) => String(row.market_key || row.market || "")).join(" ").toLowerCase();
  const chips = [];
  if (text.includes("pitcher_strikeout")) chips.push("Pitcher K Props");
  if (text.includes("home_run")) chips.push("Power Bats");
  if (text.includes("hits")) chips.push("Hits Props");
  if (text.includes("stolen_base")) chips.push("Speed Threats");
  return chips.slice(0, 4);
};

const normalizePrice = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const normalizeMarketSummary = (summary) => {
  const marketSummary = summary || {};
  const props = marketSummary?.props || {};
  const propRows = toArray(firstDefined(props.rows, props.items, []));

  return {
    moneyline: {
      away: marketSummary?.moneyline?.away
        ? {
            price: normalizePrice(marketSummary.moneyline.away.price),
            bookmaker: firstDefined(marketSummary.moneyline.away.bookmaker, marketSummary.moneyline.away.book),
          }
        : null,
      home: marketSummary?.moneyline?.home
        ? {
            price: normalizePrice(marketSummary.moneyline.home.price),
            bookmaker: firstDefined(marketSummary.moneyline.home.bookmaker, marketSummary.moneyline.home.book),
          }
        : null,
    },
    runLine: {
      away: marketSummary?.run_line?.away
        ? {
            line: firstDefined(marketSummary.run_line.away.line, marketSummary.run_line.away.handicap),
            price: normalizePrice(marketSummary.run_line.away.price),
            bookmaker: firstDefined(marketSummary.run_line.away.bookmaker, marketSummary.run_line.away.book),
          }
        : null,
      home: marketSummary?.run_line?.home
        ? {
            line: firstDefined(marketSummary.run_line.home.line, marketSummary.run_line.home.handicap),
            price: normalizePrice(marketSummary.run_line.home.price),
            bookmaker: firstDefined(marketSummary.run_line.home.bookmaker, marketSummary.run_line.home.book),
          }
        : null,
    },
    total: {
      over: marketSummary?.total?.over
        ? {
            line: firstDefined(marketSummary.total.over.line, marketSummary.total.over.total),
            price: normalizePrice(marketSummary.total.over.price),
            bookmaker: firstDefined(marketSummary.total.over.bookmaker, marketSummary.total.over.book),
          }
        : null,
      under: marketSummary?.total?.under
        ? {
            line: firstDefined(marketSummary.total.under.line, marketSummary.total.under.total),
            price: normalizePrice(marketSummary.total.under.price),
            bookmaker: firstDefined(marketSummary.total.under.bookmaker, marketSummary.total.under.book),
          }
        : null,
    },
    props: {
      rows: propRows,
      players: firstDefined(props.players, props.player_count, null),
      markets: firstDefined(props.markets, props.market_count, null),
      rowCount: propRows.length,
    },
  };
};

function SummaryCard({ label, value, hint }) {
  return (
    <div style={{ ...PANEL, padding: 14 }}>
      <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.7 }}>{label}</div>
      <div style={{ marginTop: 6, color: C.text, fontSize: 24, fontWeight: 800 }}>{value}</div>
      {hint ? <div style={{ marginTop: 4, color: "#86a091", fontSize: 12 }}>{hint}</div> : null}
    </div>
  );
}

function FixtureCard({ fixture, summary, watched, onToggleWatchlist }) {
  const awayPitcher = getProbablePitcher(summary.context, "away");
  const homePitcher = getProbablePitcher(summary.context, "home");
  const chips = inferPropChipLabels(summary.marketSummary.props.rows);

  return (
    <article style={{ ...PANEL, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <div style={{ color: C.text, fontWeight: 800, fontSize: 18 }}>
            {fixture.away_team} <span style={{ color: C.sub, fontWeight: 600 }}>at</span> {fixture.home_team}
          </div>
          <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
            <span style={{ color: "#cce3d5" }}>{fmtStatus(fixture, summary.context)}</span>
            <span style={{ color: C.sub }}>{fmtTime(fixture.kickoff_utc)}</span>
            <span style={{ color: C.sub }}>{fixture.comp || "MLB"}</span>
            {summary.context?.venue?.name ? <span style={{ color: C.sub }}>{summary.context.venue.name}</span> : null}
          </div>
        </div>
        {watched ? (
          <span style={{ color: C.amber, fontSize: 12, fontWeight: 800 }}>Watching</span>
        ) : null}
      </div>

      <div style={{ marginTop: 14, display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        <div style={{ background: C.card, borderRadius: 12, padding: 12, border: `1px solid ${C.border}` }}>
          <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>Best Moneyline</div>
          <div style={{ marginTop: 6, color: C.text, fontWeight: 700 }}>
            {summary.marketSummary.moneyline.away?.price != null ? `${fixture.away_team} ${summary.marketSummary.moneyline.away.price.toFixed(2)}` : "Away —"}
          </div>
          <div style={{ marginTop: 4, color: C.text, fontWeight: 700 }}>
            {summary.marketSummary.moneyline.home?.price != null ? `${fixture.home_team} ${summary.marketSummary.moneyline.home.price.toFixed(2)}` : "Home —"}
          </div>
        </div>

        <div style={{ background: C.card, borderRadius: 12, padding: 12, border: `1px solid ${C.border}` }}>
          <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>Run Line</div>
          <div style={{ marginTop: 6, color: C.text, fontWeight: 700 }}>
            {summary.marketSummary.runLine.away || summary.marketSummary.runLine.home
              ? `${fixture.away_team} ${summary.marketSummary.runLine.away?.line ?? "—"} @ ${summary.marketSummary.runLine.away?.price != null ? summary.marketSummary.runLine.away.price.toFixed(2) : "—"} • ${fixture.home_team} ${summary.marketSummary.runLine.home?.line ?? "—"} @ ${summary.marketSummary.runLine.home?.price != null ? summary.marketSummary.runLine.home.price.toFixed(2) : "—"}`
              : "No run line yet"}
          </div>
          <div style={{ marginTop: 4, color: "#7f998b", fontSize: 12 }}>
            {summary.marketSummary.runLine.away?.bookmaker || summary.marketSummary.runLine.home?.bookmaker || "Market pending"}
          </div>
        </div>

        <div style={{ background: C.card, borderRadius: 12, padding: 12, border: `1px solid ${C.border}` }}>
          <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>Total</div>
          <div style={{ marginTop: 6, color: C.text, fontWeight: 700 }}>
            {summary.marketSummary.total.over || summary.marketSummary.total.under
              ? `Over ${summary.marketSummary.total.over?.line ?? summary.marketSummary.total.under?.line ?? "—"} @ ${summary.marketSummary.total.over?.price != null ? summary.marketSummary.total.over.price.toFixed(2) : "—"} • Under ${summary.marketSummary.total.under?.line ?? summary.marketSummary.total.over?.line ?? "—"} @ ${summary.marketSummary.total.under?.price != null ? summary.marketSummary.total.under.price.toFixed(2) : "—"}`
              : "No total yet"}
          </div>
          <div style={{ marginTop: 4, color: "#7f998b", fontSize: 12 }}>
            {summary.marketSummary.total.over?.bookmaker || summary.marketSummary.total.under?.bookmaker || "Market pending"}
          </div>
        </div>

        <div style={{ background: C.card, borderRadius: 12, padding: 12, border: `1px solid ${C.border}` }}>
          <div style={{ color: C.sub, fontSize: 11, textTransform: "uppercase" }}>Probable Pitchers</div>
          <div style={{ marginTop: 6, color: C.text, fontWeight: 700 }}>{awayPitcher?.full_name || "Away TBD"}</div>
          <div style={{ marginTop: 4, color: C.text, fontWeight: 700 }}>{homePitcher?.full_name || "Home TBD"}</div>
        </div>
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ color: C.green, fontWeight: 800, fontSize: 13 }}>
          {summary.marketSummary.props.rowCount} player prop{summary.marketSummary.props.rowCount === 1 ? "" : "s"} available
        </span>
        {summary.marketSummary.props.players != null ? <span style={{ color: C.sub, fontSize: 12 }}>{summary.marketSummary.props.players} players priced</span> : null}
        {summary.marketSummary.props.markets != null ? <span style={{ color: C.sub, fontSize: 12 }}>{summary.marketSummary.props.markets} markets</span> : null}
        {chips.map((chip) => (
          <span
            key={chip}
            style={{
              border: `1px solid ${C.border}`,
              borderRadius: 999,
              padding: "4px 10px",
              fontSize: 11,
              color: "#caead8",
              background: "rgba(255,255,255,0.04)",
              fontWeight: 700,
            }}
          >
            {chip}
          </span>
        ))}
      </div>

      <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Link to={`/baseball/game/${fixture.id}`} style={{ ...PRIMARY, display: "inline-flex", alignItems: "center", textDecoration: "none" }}>
          Open Matchup
        </Link>
        <Link to={`/baseball/game/${fixture.id}?tab=props`} style={{ ...BUTTON, display: "inline-flex", alignItems: "center", textDecoration: "none" }}>
          View Props
        </Link>
        <button type="button" style={BUTTON} onClick={() => onToggleWatchlist(fixture.id)}>
          {watched ? "Remove Watchlist" : "Add to Watchlist"}
        </button>
      </div>
    </article>
  );
}

export default function Baseball() {
  const [day, setDay] = useState(todayISO());
  const [fixtures, setFixtures] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(WATCHLIST_KEY);
      setWatchlist(raw ? JSON.parse(raw) : []);
    } catch {
      setWatchlist([]);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(WATCHLIST_KEY, JSON.stringify(watchlist));
    } catch {
      // noop
    }
  }, [watchlist]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    fetchDailyFixtures(day, "baseball")
      .then(async (data) => {
        if (cancelled) return;
        const rows = toArray(data?.fixtures);
        setFixtures(rows);

        const summaryEntries = await Promise.all(
          rows.map(async (fixture) => {
            const contextResult = await Promise.allSettled([fetchBaseballFixtureContext(fixture.id)]);
            const context = contextResult[0]?.status === "fulfilled" ? contextResult[0].value : null;

            return [
              String(fixture.id),
              {
                context,
                marketSummary: normalizeMarketSummary(context?.market_summary),
              },
            ];
          })
        );

        if (!cancelled) {
          setSummaries(Object.fromEntries(summaryEntries));
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || "Could not load baseball fixtures.");
          setFixtures([]);
          setSummaries({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [day]);

  const spotlightFixtures = useMemo(() => {
    return [...fixtures]
      .sort((a, b) => {
        const aSummary = summaries[String(a.id)] || {};
        const bSummary = summaries[String(b.id)] || {};
        const aScore =
          (aSummary.marketSummary?.props?.rowCount || 0) +
          (getProbablePitcher(aSummary.context, "home") ? 2 : 0) +
          (getProbablePitcher(aSummary.context, "away") ? 2 : 0);
        const bScore =
          (bSummary.marketSummary?.props?.rowCount || 0) +
          (getProbablePitcher(bSummary.context, "home") ? 2 : 0) +
          (getProbablePitcher(bSummary.context, "away") ? 2 : 0);
        return bScore - aScore;
      })
      .slice(0, 5);
  }, [fixtures, summaries]);

  const toggleWatchlist = (fixtureId) => {
    const key = String(fixtureId);
    setWatchlist((prev) => (prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]));
  };

  return (
    <div style={{ minHeight: "100vh", background: C.page, color: C.text }}>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "24px 18px 50px" }}>
        <section style={{ ...PANEL, padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <div style={{ color: C.green, fontSize: 12, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase" }}>
                MLB Terminal
              </div>
              <h1 style={{ margin: "8px 0 0", fontSize: 32 }}>Baseball Matchup Intelligence</h1>
              <p style={{ margin: "8px 0 0", color: C.sub, maxWidth: 760, lineHeight: 1.5 }}>
                A premium matchup board for MLB fixtures, probable pitcher duels, team markets, and player props. Scan the slate, pick out tonight&apos;s spotlights, and jump straight into matchup detail.
              </p>
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <label style={{ color: C.sub, fontSize: 12 }}>Date</label>
              <input
                type="date"
                value={day}
                onChange={(e) => setDay(e.target.value)}
                style={{
                  height: 40,
                  borderRadius: 12,
                  border: `1px solid ${C.border}`,
                  background: "rgba(248,250,252,0.96)",
                  color: "#08130f",
                  padding: "0 12px",
                }}
              />
            </div>
          </div>

          <div style={{ marginTop: 18, display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            <SummaryCard label="Games On Board" value={fixtures.length} hint="Daily baseball slate" />
            <SummaryCard label="Spotlight Games" value={spotlightFixtures.length} hint="Matchups with the richest context" />
            <SummaryCard label="Watchlist" value={watchlist.length} hint="Pinned baseball games" />
            <SummaryCard
              label="Props Loaded"
              value={fixtures.reduce((sum, fixture) => sum + (summaries[String(fixture.id)]?.marketSummary?.props?.rowCount || 0), 0)}
              hint="Player props discovered for this slate"
            />
          </div>
        </section>

        <section style={{ marginTop: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 22 }}>Tonight&apos;s Spotlight</h2>
              <div style={{ marginTop: 4, color: C.sub, fontSize: 13 }}>
                Featured matchups with probable pitcher signal, props depth, and market context.
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12, display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))" }}>
            {spotlightFixtures.map((fixture) => {
              const summary = summaries[String(fixture.id)] || { context: null, marketSummary: normalizeMarketSummary(null) };
              const awayPitcher = getProbablePitcher(summary.context, "away");
              const homePitcher = getProbablePitcher(summary.context, "home");
              return (
                <div key={`spotlight-${fixture.id}`} style={{ ...PANEL, padding: 14 }}>
                  <div style={{ fontWeight: 800, fontSize: 16 }}>
                    {fixture.away_team} at {fixture.home_team}
                  </div>
                  <div style={{ marginTop: 6, color: C.sub, fontSize: 12 }}>{fmtTime(fixture.kickoff_utc)} • {fmtStatus(fixture, summary.context)}</div>
                  <div style={{ marginTop: 10, color: C.text, fontSize: 13 }}>
                    {awayPitcher?.full_name || "Away TBD"} vs {homePitcher?.full_name || "Home TBD"}
                  </div>
                  <div style={{ marginTop: 8, color: "#d7e7dd", fontSize: 13 }}>
                    {summary.marketSummary.moneyline.away?.price != null || summary.marketSummary.moneyline.home?.price != null
                      ? `${fixture.away_team} ${summary.marketSummary.moneyline.away?.price != null ? summary.marketSummary.moneyline.away.price.toFixed(2) : "—"} • ${fixture.home_team} ${summary.marketSummary.moneyline.home?.price != null ? summary.marketSummary.moneyline.home.price.toFixed(2) : "—"}`
                      : "Moneyline pending"}
                  </div>
                  <div style={{ marginTop: 10, color: C.green, fontWeight: 800, fontSize: 13 }}>
                    {summary.marketSummary.props.rowCount} props loaded
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <Link to={`/baseball/game/${fixture.id}`} style={{ ...PRIMARY, display: "inline-flex", alignItems: "center", textDecoration: "none" }}>
                      Open Matchup
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section style={{ marginTop: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22 }}>Full Fixture Board</h2>
            <div style={{ marginTop: 4, color: C.sub, fontSize: 13 }}>
              Premium, matchup-driven game cards with pitcher context, team markets, props availability, and quick actions.
            </div>
          </div>

          {error ? <div style={{ marginTop: 12, color: "#fca5a5" }}>{error}</div> : null}
          {loading ? <div style={{ marginTop: 12, color: C.sub }}>Loading baseball board…</div> : null}
          {!loading && !fixtures.length ? (
            <div style={{ marginTop: 12, color: C.sub }}>No baseball fixtures returned for this date.</div>
          ) : (
            <div style={{ marginTop: 12, display: "grid", gap: 14 }}>
              {fixtures.map((fixture) => (
                <FixtureCard
                  key={fixture.id}
                  fixture={fixture}
                  summary={summaries[String(fixture.id)] || { context: null, marketSummary: normalizeMarketSummary(null) }}
                  watched={watchlist.includes(String(fixture.id))}
                  onToggleWatchlist={toggleWatchlist}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
